/**
 * BIRHAUS Miller's Law Validator
 * 
 * Automated testing utilities to validate Miller's Law (7±2) compliance
 * across all BIRHAUS components and applications.
 * 
 * Features:
 * - Navigation item counting and validation
 * - Form field cognitive load analysis
 * - Select option overflow detection
 * - Tab count validation (4-3-1 rule)
 * - Real-time cognitive load monitoring
 */

// import { screen, within } from '@testing-library/react' // Available for future test extensions

export interface MillersLawViolation {
  type: 'navigation' | 'form' | 'select' | 'tabs' | 'actions'
  element: Element
  count: number
  maxRecommended: number
  severity: 'low' | 'medium' | 'high' | 'critical'
  message: string
  messageEs: string
  birhausPrinciple: number
  suggestions: string[]
}

export interface MillersLawTestOptions {
  // Cognitive limits
  maxNavigationItems?: number
  maxFormFields?: number
  maxSelectOptions?: number
  maxTabs?: number
  maxActions?: number
  
  // Test configuration
  strict?: boolean
  includeDisabled?: boolean
  checkHidden?: boolean
  
  // Selectors for custom components
  navigationSelector?: string
  formSelector?: string
  selectSelector?: string
  tabSelector?: string
  actionSelector?: string
}

export class MillersLawValidator {
  private options: Required<MillersLawTestOptions>
  
  constructor(options: MillersLawTestOptions = {}) {
    this.options = {
      maxNavigationItems: 7,
      maxFormFields: 7,
      maxSelectOptions: 7,
      maxTabs: 4,
      maxActions: 4,
      strict: false,
      includeDisabled: false,
      checkHidden: false,
      navigationSelector: 'nav, [role="navigation"], [data-birhaus-component="navigation"]',
      formSelector: 'form, [data-birhaus-component="form"]',
      selectSelector: 'select, [data-birhaus-component="select"]',
      tabSelector: '[role="tablist"], [data-birhaus-component="tabs"]',
      actionSelector: 'button, [role="button"], a[href], [data-birhaus-component="button"]',
      ...options
    }
  }
  
  /**
   * Validate all Miller's Law compliance issues in the current DOM
   */
  validateAll(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    
    violations.push(...this.validateNavigation(container))
    violations.push(...this.validateForms(container))
    violations.push(...this.validateSelects(container))
    violations.push(...this.validateTabs(container))
    violations.push(...this.validateActions(container))
    
    return violations
  }
  
  /**
   * Validate navigation items (BIRHAUS Principle #1)
   */
  validateNavigation(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    const navElements = container.querySelectorAll(this.options.navigationSelector)
    
    navElements.forEach(nav => {
      const items = this.getNavigationItems(nav)
      
      if (items.length > this.options.maxNavigationItems) {
        const severity = this.getSeverity(items.length, this.options.maxNavigationItems)
        
        violations.push({
          type: 'navigation',
          element: nav,
          count: items.length,
          maxRecommended: this.options.maxNavigationItems,
          severity,
          message: `Navigation has ${items.length} items (Miller's Law recommends max ${this.options.maxNavigationItems})`,
          messageEs: `Navegación tiene ${items.length} elementos (Ley de Miller recomienda máximo ${this.options.maxNavigationItems})`,
          birhausPrinciple: 1,
          suggestions: [
            'Group related navigation items into dropdown menus',
            'Use progressive disclosure to hide secondary navigation',
            'Consider using breadcrumbs for deep navigation',
            'Implement search functionality for large menus'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate form field counts (BIRHAUS Principle #4)
   */
  validateForms(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    const formElements = container.querySelectorAll(this.options.formSelector)
    
    formElements.forEach(form => {
      const fields = this.getFormFields(form)
      
      if (fields.length > this.options.maxFormFields) {
        const severity = this.getSeverity(fields.length, this.options.maxFormFields)
        
        violations.push({
          type: 'form',
          element: form,
          count: fields.length,
          maxRecommended: this.options.maxFormFields,
          severity,
          message: `Form has ${fields.length} fields (Miller's Law recommends max ${this.options.maxFormFields})`,
          messageEs: `Formulario tiene ${fields.length} campos (Ley de Miller recomienda máximo ${this.options.maxFormFields})`,
          birhausPrinciple: 4,
          suggestions: [
            'Split form into multiple steps using BirhausForm',
            'Use progressive disclosure to reveal fields conditionally',
            'Group related fields into collapsible sections',
            'Consider using a wizard pattern for complex forms'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate select option counts
   */
  validateSelects(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    const selectElements = container.querySelectorAll(this.options.selectSelector)
    
    selectElements.forEach(select => {
      const options = this.getSelectOptions(select)
      
      if (options.length > this.options.maxSelectOptions) {
        const severity = this.getSeverity(options.length, this.options.maxSelectOptions)
        
        violations.push({
          type: 'select',
          element: select,
          count: options.length,
          maxRecommended: this.options.maxSelectOptions,
          severity,
          message: `Select has ${options.length} options (Miller's Law recommends max ${this.options.maxSelectOptions})`,
          messageEs: `Lista desplegable tiene ${options.length} opciones (Ley de Miller recomienda máximo ${this.options.maxSelectOptions})`,
          birhausPrinciple: 1,
          suggestions: [
            'Use BirhausCombobox with search functionality',
            'Group options into categories',
            'Implement progressive filtering',
            'Consider using autocomplete instead of select'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate tab counts (BIRHAUS 4-3-1 Rule)
   */
  validateTabs(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    const tabElements = container.querySelectorAll(this.options.tabSelector)
    
    tabElements.forEach(tabContainer => {
      const tabs = this.getTabs(tabContainer)
      
      if (tabs.length > this.options.maxTabs) {
        const severity = this.getSeverity(tabs.length, this.options.maxTabs)
        
        violations.push({
          type: 'tabs',
          element: tabContainer,
          count: tabs.length,
          maxRecommended: this.options.maxTabs,
          severity,
          message: `Tabs have ${tabs.length} items (BIRHAUS 4-3-1 rule recommends max ${this.options.maxTabs})`,
          messageEs: `Pestañas tienen ${tabs.length} elementos (regla BIRHAUS 4-3-1 recomienda máximo ${this.options.maxTabs})`,
          birhausPrinciple: 1,
          suggestions: [
            'Use BirhausTabs with overflow dropdown',
            'Group related tabs into sections',
            'Consider using accordion instead of tabs',
            'Implement nested navigation structure'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate action button counts
   */
  validateActions(container: HTMLElement = document.body): MillersLawViolation[] {
    const violations: MillersLawViolation[] = []
    
    // Check each form, dialog, or card for action overload
    const containers = container.querySelectorAll('form, [role="dialog"], [data-birhaus-component="card"], .actions')
    
    containers.forEach(actionContainer => {
      const actions = this.getActions(actionContainer)
      
      if (actions.length > this.options.maxActions) {
        const severity = this.getSeverity(actions.length, this.options.maxActions)
        
        violations.push({
          type: 'actions',
          element: actionContainer,
          count: actions.length,
          maxRecommended: this.options.maxActions,
          severity,
          message: `Container has ${actions.length} actions (BIRHAUS 4-3-1 rule recommends max ${this.options.maxActions})`,
          messageEs: `Contenedor tiene ${actions.length} acciones (regla BIRHAUS 4-3-1 recomienda máximo ${this.options.maxActions})`,
          birhausPrinciple: 3,
          suggestions: [
            'Combine related actions into dropdown menus',
            'Use progressive disclosure for secondary actions',
            'Implement one clear primary action pattern',
            'Move less important actions to overflow menu'
          ]
        })
      }
    })
    
    return violations
  }
  
  /**
   * Get navigation items from a navigation element
   */
  private getNavigationItems(nav: Element): Element[] {
    const items = nav.querySelectorAll('a, button, [role="menuitem"], [role="link"]')
    return this.filterElements(Array.from(items))
  }
  
  /**
   * Get form fields from a form element
   */
  private getFormFields(form: Element): Element[] {
    const fields = form.querySelectorAll('input, select, textarea, [role="textbox"], [role="combobox"], [role="listbox"]')
    return this.filterElements(Array.from(fields))
  }
  
  /**
   * Get options from a select element
   */
  private getSelectOptions(select: Element): Element[] {
    if (select.tagName.toLowerCase() === 'select') {
      const options = select.querySelectorAll('option')
      return this.filterElements(Array.from(options))
    }
    
    // For custom select components
    const options = select.querySelectorAll('[role="option"], [data-value]')
    return this.filterElements(Array.from(options))
  }
  
  /**
   * Get tabs from a tab container
   */
  private getTabs(tabContainer: Element): Element[] {
    const tabs = tabContainer.querySelectorAll('[role="tab"], [data-birhaus-component="tab"]')
    return this.filterElements(Array.from(tabs))
  }
  
  /**
   * Get action buttons from a container
   */
  private getActions(container: Element): Element[] {
    const actions = container.querySelectorAll(this.options.actionSelector)
    return this.filterElements(Array.from(actions))
  }
  
  /**
   * Filter elements based on options (hidden, disabled, etc.)
   */
  private filterElements(elements: Element[]): Element[] {
    return elements.filter(element => {
      // Skip disabled elements if not included
      if (!this.options.includeDisabled && element.hasAttribute('disabled')) {
        return false
      }
      
      // Skip hidden elements if not included
      if (!this.options.checkHidden) {
        const style = getComputedStyle(element)
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false
        }
      }
      
      return true
    })
  }
  
  /**
   * Calculate severity based on how much the count exceeds the limit
   */
  private getSeverity(count: number, maxRecommended: number): 'low' | 'medium' | 'high' | 'critical' {
    const excess = count - maxRecommended
    const excessPercentage = (excess / maxRecommended) * 100
    
    if (excessPercentage > 100) return 'critical' // More than double
    if (excessPercentage > 50) return 'high'      // 50%+ over limit
    if (excessPercentage > 25) return 'medium'    // 25-50% over limit
    return 'low'                                  // Up to 25% over limit
  }
}

/**
 * Test helper functions for common assertions
 */

/**
 * Assert that Miller's Law is followed for navigation
 */
export function expectNavigationCompliance(
  container?: HTMLElement,
  maxItems: number = 7
): void {
  const validator = new MillersLawValidator({ maxNavigationItems: maxItems })
  const violations = validator.validateNavigation(container)
  
  if (violations.length > 0) {
    const violation = violations[0]
    throw new Error(
      `Miller's Law violation: ${violation.message}\n` +
      `Suggestions: ${violation.suggestions.join(', ')}`
    )
  }
}

/**
 * Assert that forms follow Miller's Law for field count
 */
export function expectFormCompliance(
  container?: HTMLElement,
  maxFields: number = 7
): void {
  const validator = new MillersLawValidator({ maxFormFields: maxFields })
  const violations = validator.validateForms(container)
  
  if (violations.length > 0) {
    const violation = violations[0]
    throw new Error(
      `Miller's Law violation: ${violation.message}\n` +
      `Suggestions: ${violation.suggestions.join(', ')}`
    )
  }
}

/**
 * Assert that the 4-3-1 rule is followed for tabs
 */
export function expectTabCompliance(
  container?: HTMLElement,
  maxTabs: number = 4
): void {
  const validator = new MillersLawValidator({ maxTabs })
  const violations = validator.validateTabs(container)
  
  if (violations.length > 0) {
    const violation = violations[0]
    throw new Error(
      `BIRHAUS 4-3-1 Rule violation: ${violation.message}\n` +
      `Suggestions: ${violation.suggestions.join(', ')}`
    )
  }
}

/**
 * Assert overall Miller's Law compliance
 */
export function expectMillersLawCompliance(
  container?: HTMLElement,
  options?: MillersLawTestOptions
): void {
  const validator = new MillersLawValidator(options)
  const violations = validator.validateAll(container)
  
  if (violations.length > 0) {
    const criticalViolations = violations.filter(v => v.severity === 'critical')
    const highViolations = violations.filter(v => v.severity === 'high')
    
    if (criticalViolations.length > 0 || highViolations.length > 0) {
      const errorMessages = [...criticalViolations, ...highViolations]
        .map(v => `${v.type}: ${v.message}`)
        .join('\n')
      
      throw new Error(
        `Miller's Law violations detected:\n${errorMessages}\n\n` +
        `Total violations: ${violations.length} (${criticalViolations.length} critical, ${highViolations.length} high)`
      )
    }
  }
}

